import React, { useState, useEffect } from 'react';
import { api } from '../api';

const SOURCE_TYPES = [
  { value: 'navigator', label: 'Sales Navigator', subtitle: 'Scrape a saved list or search URL', accent: 'blue' },
  { value: 'file', label: 'File Upload', subtitle: 'Import from CSV, XLSX, or TSV', accent: 'emerald' },
  { value: 'hubspot', label: 'HubSpot CRM', subtitle: 'Sync all contacts from HubSpot', accent: 'orange' },
];

const ACCENT = {
  blue: { active: 'border-blue-500/40 bg-blue-500/5', pill: 'text-blue-400 bg-blue-500/10' },
  emerald: { active: 'border-emerald-500/40 bg-emerald-500/5', pill: 'text-emerald-400 bg-emerald-500/10' },
  orange: { active: 'border-orange-500/40 bg-orange-500/5', pill: 'text-orange-400 bg-orange-500/10' },
  slate: { active: 'border-slate-600', pill: 'text-slate-400 bg-slate-700' },
};

function relativeTime(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

function typeBadge(type) {
  if (type === 'sales_nav' || type === 'SALES_NAV') return { label: 'Sales Navigator', accent: 'blue' };
  if (type === 'hubspot' || type === 'HUBSPOT') return { label: 'HubSpot', accent: 'orange' };
  if (type === 'file_upload' || type === 'FILE_UPLOAD') return { label: 'File Upload', accent: 'emerald' };
  return { label: String(type || 'Unknown'), accent: 'slate' };
}

export default function SourcesPanel({ onRefresh }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceType, setSourceType] = useState('navigator');
  const [formData, setFormData] = useState({ name: '', url: '', intro_network_id: '' });
  const [hubspotImporting, setHubspotImporting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [sortBy, setSortBy] = useState('last_synced');
  const [introNetwork, setIntroNetwork] = useState([]);
  const [connStatus, setConnStatus] = useState(null);
  const [connBusy, setConnBusy] = useState(false);

  useEffect(() => {
    loadSources();
    loadIntroNetwork();
    loadConnStatus();
    const iv = setInterval(() => { loadSources(); loadConnStatus(); }, 15000);
    return () => clearInterval(iv);
  }, []);

  const loadConnStatus = async () => {
    try { setConnStatus(await api.fetch('/api/agent-jobs/connections-status')); }
    catch (err) { console.error(err); }
  };

  // Queue a my-connections pull on this user's own desktop agent.
  // mode 'full' walks the whole list; 'delta' stops at what we already know.
  const handleConnectionsSync = async (mode) => {
    try {
      setConnBusy(true); setError('');
      await api.enqueueAgentJob({ job_type: 'scrape_connections', payload: { mode } });
      flash(mode === 'delta' ? 'Refetch queued on your agent.' : 'Full connections pull queued on your agent.');
      await loadConnStatus();
    } catch (err) { setError(err.message); }
    finally { setConnBusy(false); }
  };

  const loadIntroNetwork = async () => {
    try { const data = await api.fetch('/api/intro-network'); setIntroNetwork(data || []); } catch (err) { console.error(err); }
  };

  const handleLinkIntro = async (sourceId, introNetworkId) => {
    try {
      await api.fetch(`/api/sources/${sourceId}`, { method: 'PUT', body: JSON.stringify({ intro_network_id: introNetworkId || null }) });
      await loadSources();
    } catch (err) { setError(err.message); }
  };

  const loadSources = async () => {
    try { const data = await api.getSources(); setSources(data || []); setError(''); } catch (err) { setError(err.message); }
  };

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 5000); };

  const handleAddSource = async (e) => {
    e?.preventDefault?.();
    if (!formData.name.trim()) { setError('Name is required'); return; }
    try {
      setLoading(true); setError('');
      if (sourceType === 'navigator') {
        if (!formData.url.trim()) { setError('Sales Navigator URL is required'); setLoading(false); return; }
        const created = await api.createSource({
          name: formData.name.trim(),
          type: 'sales_nav',
          config: { url: formData.url.trim() },
        });
        // If the rep tagged the source as a team member's connection list,
        // link it immediately. Each lead ingested from this source will
        // then auto-create a lead_intros row attributing the warm intro.
        if (formData.intro_network_id && created?.id) {
          await handleLinkIntro(created.id, formData.intro_network_id);
        }
        flash(`Added "${formData.name.trim()}"`);
      } else {
        await api.createSource({ name: formData.name.trim(), type: 'file_upload', config: {} });
        flash(`Source "${formData.name.trim()}" created.`);
      }
      setFormData({ name: '', url: '', intro_network_id: '' });
      await loadSources(); onRefresh?.();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.currentTarget.files?.[0];
    if (!file || !formData.name.trim()) { setError('Please provide a source name before uploading.'); return; }
    try {
      setLoading(true); setError('');
      const result = await api.uploadFile(file, formData.name.trim());
      flash(`Uploaded "${file.name}" (${result?.rows ?? '?'} rows).`);
      setFormData({ name: '', url: '' });
      await loadSources(); onRefresh?.();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); e.currentTarget.value = ''; }
  };

  const handleHubspotImport = async () => {
    try {
      setHubspotImporting(true); setError('');
      const result = await api.fetch('/api/import/hubspot', { method: 'POST' });
      flash(`HubSpot import queued (${result.job_id}).`);
      await loadSources(); onRefresh?.();
    } catch (err) { setError(err.message); }
    finally { setHubspotImporting(false); }
  };

  const handleDeleteSource = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.deleteSource(id); await loadSources(); onRefresh?.(); } catch (err) { setError(err.message); }
  };

  const sorted = [...sources].sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'lead_count') return (b.lead_count || 0) - (a.lead_count || 0);
    if (sortBy === 'created_at') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    const la = a.last_synced_at ? new Date(a.last_synced_at).getTime() : 0;
    const lb = b.last_synced_at ? new Date(b.last_synced_at).getTime() : 0;
    return lb - la;
  });

  const selected = SOURCE_TYPES.find(t => t.value === sourceType) || SOURCE_TYPES[0];

  return (
    <div className="h-full overflow-y-auto scrollable-content">
      <div className="max-w-4xl mx-auto px-8 py-6 space-y-8">

        {/* Add Source */}
        <div className="border border-slate-800 rounded-lg p-6">
          <h2 className="text-sm font-medium text-slate-200 mb-1">Add Source</h2>
          <p className="text-xs text-slate-600 mb-5">Pull leads from Sales Navigator, a file, or HubSpot.</p>

          {error && <div className="mb-4 p-2.5 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm">{error}</div>}
          {successMsg && <div className="mb-4 p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-md text-emerald-400 text-sm">{successMsg}</div>}

          {/* Type selector */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            {SOURCE_TYPES.map((t) => {
              const isActive = sourceType === t.value;
              const a = ACCENT[t.accent];
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => { setSourceType(t.value); setError(''); }}
                  className={`text-left p-3 rounded-md border transition-colors ${
                    isActive ? a.active : 'border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="text-sm font-medium text-slate-200">{t.label}</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">{t.subtitle}</div>
                </button>
              );
            })}
          </div>

          {/* Form */}
          {sourceType === 'hubspot' ? (
            <div className="p-4 border border-slate-800 rounded-md">
              <p className="text-xs text-slate-500 mb-3">
                Make sure your HubSpot Private App token is set in Settings.
              </p>
              <button type="button" onClick={handleHubspotImport} disabled={hubspotImporting}
                className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 rounded-md text-sm transition-colors">
                {hubspotImporting ? 'Starting import...' : 'Import all HubSpot contacts'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleAddSource} className="space-y-3">
              <div>
                <label className="block text-[11px] text-slate-500 mb-1.5">Source Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={sourceType === 'navigator' ? 'e.g., Enterprise CISOs' : 'e.g., Q1 Prospects'}
                  className="w-full bg-slate-800/60 text-slate-200 px-3 py-2 rounded-md border border-slate-700/50 focus:border-slate-600 focus:outline-none text-sm placeholder-slate-600" />
              </div>

              {sourceType === 'navigator' ? (
                <>
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1.5">Sales Navigator URL</label>
                    <input type="url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://www.linkedin.com/sales/lists/..."
                      className="w-full bg-slate-800/60 text-slate-200 px-3 py-2 rounded-md border border-slate-700/50 focus:border-slate-600 focus:outline-none text-sm placeholder-slate-600" />
                  </div>
                  {/* Optional: tag this source as "X's connections" so every
                      ingested lead gets a warm-intro link to that team member.
                      Replaces the old dedicated connections scraper. */}
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1.5">
                      Connections of (optional)
                    </label>
                    <select
                      value={formData.intro_network_id}
                      onChange={(e) => setFormData({ ...formData, intro_network_id: e.target.value })}
                      className="w-full bg-slate-800/60 text-slate-200 px-3 py-2 rounded-md border border-slate-700/50 focus:border-slate-600 focus:outline-none text-sm"
                    >
                      <option value="">— not a connection set —</option>
                      {introNetwork.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                    {formData.intro_network_id && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        Every lead pulled from this source will be marked as a warm intro via this team member.
                      </p>
                    )}
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 rounded-md text-sm font-medium transition-colors">
                    {loading ? 'Adding...' : 'Add Source'}
                  </button>
                </>
              ) : (
                <div>
                  <label htmlFor="fileInput"
                    className={`block border border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
                      formData.name.trim() ? 'border-slate-700 hover:border-slate-600' : 'border-slate-800 opacity-50 cursor-not-allowed'
                    }`}>
                    <p className="text-slate-400 text-sm">{formData.name.trim() ? 'Click to upload' : 'Enter a source name first'}</p>
                    <p className="text-[11px] text-slate-600 mt-1">CSV, XLSX, XLS, or TSV</p>
                  </label>
                  <input type="file" onChange={handleFileUpload} accept=".csv,.xlsx,.xls,.tsv"
                    disabled={loading || !formData.name.trim()} className="hidden" id="fileInput" />
                </div>
              )}
            </form>
          )}
        </div>

        {/* My Connections — the rep's own 1st-degree network, pulled by
            their desktop agent straight from /mynetwork. No Sales Nav. */}
        <div className="border border-slate-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-medium text-slate-200 mb-1">My Connections</h2>
              <p className="text-xs text-slate-600">
                {connStatus?.connection_count
                  ? `${connStatus.connection_count.toLocaleString()} connections synced`
                  : 'Not synced yet'}
                {connStatus?.last_sync_at && (
                  <span> · last sync {relativeTime(connStatus.last_sync_at)}</span>
                )}
                {connStatus && !connStatus.agent_online && (
                  <span className="text-amber-500"> · agent offline — open the Siphon Agent app</span>
                )}
              </p>
            </div>
            <div className="flex gap-2 items-center">
              {connStatus?.active_job ? (
                (() => {
                  const aj = connStatus.active_job;
                  const done = aj.processed_items || 0;
                  const total = aj.total_items || 0;
                  const pct = total
                    ? Math.min(100, Math.round((done / total) * 100))
                    : Math.round((aj.progress || 0) * 100);
                  if (aj.status !== 'running') {
                    return <span className="text-xs text-slate-400 px-3 py-2">Queued — waiting for your agent…</span>;
                  }
                  return (
                    <div className="min-w-[220px] px-3 py-2">
                      <div className="flex justify-between text-[11px] text-slate-400 mb-1">
                        <span>Syncing connections…</span>
                        <span className="tabular-nums">
                          {done.toLocaleString()}{total ? ` / ${total.toLocaleString()}` : ''} ({pct}%)
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })()
              ) : (
                <>
                  <button type="button" disabled={connBusy}
                    onClick={() => handleConnectionsSync('delta')}
                    className="px-3 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 rounded-md text-xs font-medium transition-colors">
                    Refetch new
                  </button>
                  {/* Full-network seed is the LinkedIn CSV export (File Upload above),
                      not a live scrape — so no "Full pull" here. */}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sources Table */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-slate-200">
              Sources <span className="text-slate-600">({sources.length})</span>
            </h2>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-slate-600">Sort</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="bg-slate-800/60 border border-slate-700/50 text-slate-400 text-[11px] rounded-md px-2 py-1 outline-none">
                <option value="last_synced">Last scraped</option>
                <option value="created_at">Date added</option>
                <option value="name">Name</option>
                <option value="lead_count">Lead count</option>
              </select>
            </div>
          </div>

          {sorted.length === 0 ? (
            <div className="text-center py-10 text-slate-600 text-sm border border-dashed border-slate-800 rounded-lg">
              No sources yet. Add one above to get started.
            </div>
          ) : (
            <div className="border border-slate-800 rounded-lg overflow-x-auto scrollable-content">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="text-left text-[11px] text-slate-500 border-b border-slate-800">
                    <th className="py-2.5 px-4 font-medium">Name</th>
                    <th className="py-2.5 px-3 font-medium">Type</th>
                    <th className="py-2.5 px-3 font-medium text-right">Leads</th>
                    <th className="py-2.5 px-3 font-medium">Last scraped</th>
                    <th className="py-2.5 px-3 font-medium">Added</th>
                    <th className="py-2.5 px-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((source) => {
                    const badge = typeBadge(source.type);
                    const badgeAccent = ACCENT[badge.accent];
                    const rel = relativeTime(source.last_synced_at);
                    const isSalesNav = source.type === 'sales_nav' || source.type === 'SALES_NAV';
                    return (
                      <tr key={source.id} className="border-b border-slate-800/50 last:border-0 hover:bg-slate-800/20 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-200">{source.name}</span>
                            {source.intro_network_id && (
                              <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                                {introNetwork.find(n => n.id === source.intro_network_id)?.name || 'Linked'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {source.config?.url && (
                              <a href={source.config.url} target="_blank" rel="noreferrer"
                                className="text-[11px] text-slate-600 hover:text-slate-400 truncate block max-w-[280px]"
                                onClick={(e) => e.stopPropagation()}>{source.config.url}</a>
                            )}
                            {isSalesNav && introNetwork.length > 0 && (
                              <select value={source.intro_network_id || ''} onChange={(e) => handleLinkIntro(source.id, e.target.value)}
                                className="text-[11px] bg-transparent text-slate-600 border border-slate-800 rounded px-1.5 py-0.5 cursor-pointer hover:text-slate-400">
                                <option value="">No intro link</option>
                                {introNetwork.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                              </select>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`text-[11px] px-2 py-0.5 rounded ${badgeAccent.pill}`}>{badge.label}</span>
                        </td>
                        <td className="py-3 px-3 text-right text-slate-400 tabular-nums">
                          {source.intro_network_id ? (
                            <div>
                              <span className="text-xs">{(source.connection_count || 0).toLocaleString()} conn</span>
                              {source.intros_matched > 0 && (
                                <div className="text-[11px] text-emerald-500">{source.intros_matched} matched</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs">{source.lead_count != null ? source.lead_count.toLocaleString() : '--'}</span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {rel ? (
                            <span className="text-xs text-slate-400">{rel}</span>
                          ) : (
                            <span className="text-[11px] text-slate-700">never</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-[11px] text-slate-600">
                          {source.created_at ? new Date(source.created_at).toLocaleDateString() : '--'}
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-1.5 justify-end">
                            {/* Scraping moved to the desktop agent app. Reps trigger
                                scrapes locally from the agent's window. */}
                            <button onClick={() => handleDeleteSource(source.id, source.name)}
                              className="px-2.5 py-1.5 text-slate-600 hover:text-red-400 text-xs transition-colors">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Scrape modal removed — scraping is now handled by the desktop agent. */}
      </div>
    </div>
  );
}
